/**
 * Check if a card matches a search query.
 * Handles compact card number formats like "op05119" → matches "OP05-119".
 * Supports multi-word queries with AND logic: "eb03 nami" matches EB03 cards named Nami.
 */
export function matchCardSearch(query: string, card: { id?: string; name?: string; number?: string; attributes?: string; family?: string }): boolean {
  const q = query.toLowerCase().trim()
  if (!q) return false

  const words = q.split(/\s+/)

  // For multi-word queries, every word must match somewhere
  if (words.length > 1) {
    return words.every(word => matchSingleTerm(word, card))
  }

  return matchSingleTerm(q, card)
}

function matchSingleTerm(term: string, card: { id?: string; name?: string; number?: string; attributes?: string; family?: string }): boolean {
  const idLower = card.id?.toLowerCase() || ''
  const nameLower = card.name?.toLowerCase() || ''
  const numberLower = card.number?.toLowerCase() || ''
  const attrsLower = card.attributes?.toLowerCase() || ''
  const familyLower = card.family?.toLowerCase() || ''

  if (
    nameLower.includes(term) ||
    idLower.includes(term) ||
    numberLower.includes(term) ||
    attrsLower.includes(term) ||
    familyLower.includes(term)
  ) return true

  // Space-stripped matching: "strawhat" matches "Straw Hat Crew", "monkeydluffy" matches "Monkey.D.Luffy"
  const stripped = term.replace(/[\s.]/g, '')
  if (
    nameLower.replace(/[\s.]/g, '').includes(stripped) ||
    familyLower.replace(/\s/g, '').includes(stripped) ||
    attrsLower.replace(/\s/g, '').includes(stripped)
  ) return true

  // Normalize compact card numbers: "op05119" → "op05-119", "st1001" → "st01-001"
  const m = term.match(/^([a-z]+)(\d{2,})$/)
  if (m) {
    const [, prefix, digits] = m
    if (digits.length >= 3) {
      const setNum = digits.slice(0, digits.length - 3).padStart(2, '0')
      const cardNum = digits.slice(-3)
      const normalized = `${prefix}${setNum}-${cardNum}`
      if (idLower.includes(normalized) || numberLower.includes(normalized)) return true
    }
  }

  return false
}

/**
 * Format date to DD/MM/YYYY format (European)
 */
export const formatDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date'
  }
  
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  
  return `${day}/${month}/${year}`
}

/**
 * Format date with time to DD/MM/YYYY HH:mm format
 */
export const formatDateTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date'
  }
  
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

/**
 * Format date with month name (e.g., "15 Jan 2024")
 */
export const formatDateShort = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date'
  }
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  
  const day = date.getDate()
  const month = monthNames[date.getMonth()]
  const year = date.getFullYear()
  
  return `${day} ${month} ${year}`
}

/**
 * Format currency in Euros
 */
export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) {
    return '€0.00'
  }
  
  // Format with 2 decimal places and use comma as decimal separator (European style)
  const formatted = amount.toFixed(2).replace('.', ',')
  return `€${formatted}`
}

/**
 * Format relative time (e.g., "2 hours ago", "yesterday")
 */
export const formatRelativeTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  
  if (days > 7) {
    return formatDate(date)
  } else if (days > 1) {
    return `${days} days ago`
  } else if (days === 1) {
    return 'Yesterday'
  } else if (hours > 1) {
    return `${hours} hours ago`
  } else if (hours === 1) {
    return '1 hour ago'
  } else if (minutes > 1) {
    return `${minutes} minutes ago`
  } else if (minutes === 1) {
    return '1 minute ago'
  } else {
    return 'Just now'
  }
}